/**
 * FamilyCard Component
 * Compact dashboard view of the user's family members.
 * Display + add only — detailed view, edits and deletes live in Account → Family.
 * Mirrors WeeklyTipsCard structure/tokens (Card + small icon square + text-sm).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../common/Card';
import { FaUsers, FaUserPlus } from 'react-icons/fa';
import { useFamilyMembers } from '../../hooks/useFamilyMembers';
import AddFamilyMemberModal from '../account/AddFamilyMemberModal';
import InfoTooltip from '../common/InfoTooltip';

const TOOLTIP_TEXT =
  'Coord lærer automatisk om familien din gjennom samtaler. Du kan også legge til familiemedlemmer manuelt.';

const FamilyCard = () => {
  const { familyMembers, loading, loadError, refreshing, refresh } = useFamilyMembers();
  const [showAddMember, setShowAddMember] = useState(false);

  return (
    <>
      {/* Shared add-member modal (same component as Account → Family) — hoisted
          above the loading branch so no loading/refresh state can ever unmount
          it mid-submit. refresh receives the new member's entityId from onAdded
          and retries until the Search index returns it. */}
      <AddFamilyMemberModal
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdded={refresh}
      />

      {loading ? (
        /* Initial-load skeleton — only the FIRST fetch renders this branch;
           refresh() keeps the live card mounted (see useFamilyMembers). */
        <Card variant="muted" padding="md" className="mb-6">
          <div className="flex items-start gap-3">
            <div className="skeleton-shimmer w-8 h-8 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="skeleton h-4 w-1/3" />
              <div className="skeleton h-3 w-full" />
              <div className="skeleton h-3 w-2/3" />
            </div>
          </div>
        </Card>
      ) : (
        <Card variant="transparent" padding="md" className="mb-6 border-2 border-border shadow-md">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 w-8 h-8 bg-sage-100 rounded-lg flex items-center justify-center">
              <FaUsers className="w-4 h-4 text-sage-600" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-text-primary">
                  Min familie
                </h3>

                {/* Info tooltip */}
                <InfoTooltip text={TOOLTIP_TEXT} label="Om familiemedlemmer" />
              </div>

              {/* Member badges. A failed load is an error, never "no family". */}
              {loadError ? (
                <div role="alert">
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Vi fikk ikke hentet familien din akkurat nå.
                  </p>
                  <button
                    type="button"
                    onClick={() => refresh()}
                    disabled={refreshing}
                    className="mt-1 inline-flex items-center min-h-[48px] text-sm font-medium text-sage-700 hover:text-sage-800 underline underline-offset-4 transition-colors disabled:opacity-50"
                  >
                    {refreshing ? 'Prøver igjen …' : 'Prøv igjen'}
                  </button>
                </div>
              ) : familyMembers.length === 0 ? (
                <p className="text-sm text-text-secondary leading-relaxed">
                  Ingen familiemedlemmer ennå. Coord lærer om familien din gjennom samtaler,
                  eller du kan legge til noen manuelt.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2 mt-1">
                  {familyMembers.map((member, index) => (
                    <div
                      key={`${member.entityId || member.name}-${index}`}
                      className="px-3 py-1.5 rounded-full bg-transparent border border-border max-w-full"
                    >
                      <p className="text-sm font-medium text-text-primary leading-tight truncate">
                        {member.name}
                      </p>
                      <p className="text-xs text-text-muted leading-tight">
                        {member.memories.length} minner
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add member */}
              <button
                type="button"
                onClick={() => setShowAddMember(true)}
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-sage-700 hover:text-sage-800 transition-colors"
              >
                <FaUserPlus className="w-3.5 h-3.5" />
                Legg til familiemedlem
              </button>

              {familyMembers.length > 0 && (
                <div className="mt-2">
                  <Link
                    to="/account/family"
                    className="text-xs text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                  >
                    Se alle →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default FamilyCard;
